import { TableColumnType } from 'antd';
import {
  ColumnFilterItem,
  CompareFn,
  SorterResult,
} from 'antd/lib/table/interface';
import { get } from 'lodash';
import { Key } from 'react';

/**
 * The data that represents a column sorted.
 */
export type NormalizedSorterResult = {
  [key: string]: 'ascend' | 'descend';
};

/**
 * The builder configuration
 */
export type BuilderConfig<T extends object> = {
  paginated?: boolean;
  filterConfig?: FilterConfig<T>;
};

/**
 * The filter configuration
 */
export type FilterConfig<T extends object> = {
  selectedFilters?: SelectedFilters<T>;
  filterOptions?: FilterOptions<T>;
};

/**
 * An object with keys equal a list of Key or undefined.
 */
export type SelectedFilters<T extends object> = {
  [Props in keyof T]?: Key[];
};

/**
 * An object with keys equal a list of { text, value } or undefined.
 */
export type FilterOptions<T extends object> = {
  [Props in keyof T]?: ColumnFilterItem[];
};

type DataIndex = string | number | (string | number)[];

/**
 * @param key Must be one of T keys
 * @param priority Optional. Defines the sortOrder, the lowest number is the first sorted.
 * @param dataIndex Optional. The index of the data.
 * @param filterOptions Optional. Only use if you want to filter by column.
 */
type BuildFilterableColumnArgs<T extends object> = {
  key: keyof T;
  priority?: number;
  dataIndex?: DataIndex;
  filterOptions?: FilterOptions<T>;
};

type ColumnConfig<T extends object> = TableColumnType<T> &
  BuildFilterableColumnArgs<T>;

/**
 * High-Order-Function to inject the filter config in the columns.
 * @param builderConfig Builder configuration
 * @returns FilterableColumn
 */
export function filterableColumnBuilder<T extends object>(
  builderConfig: BuilderConfig<T> = {}
) {
  return (columnConfig: ColumnConfig<T>) =>
    buildFilterableColumn(columnConfig, builderConfig);
}

/**
 * Helper to normalize the sorting data before sending it to the API.
 * @param sorterResult An object or list to be transformed in a KeyValue object.
 * @returns A NormalizedSorterResult object
 */
export function normalizeSortingData<RecordType>(
  sorterResult: SorterResult<RecordType> | SorterResult<RecordType>[]
): NormalizedSorterResult {
  return [sorterResult]
    .flat()
    .map(translateSortingData)
    .reduce((acc, next) => ({ ...acc, ...next }), {});
}

function translateSortingData<RecordType>({
  columnKey,
  order,
}: SorterResult<RecordType>): NormalizedSorterResult {
  return columnKey && order ? { [columnKey]: order } : {};
}

function buildFilterableColumn<T extends object>(
  { key, priority, dataIndex, ...rest }: ColumnConfig<T>,
  { paginated, filterConfig = {} }: BuilderConfig<T>
): TableColumnType<T> {
  const dataIndexKey = dataIndex || key;
  const sorterAttributes = buildSortableAttributes(
    dataIndexKey,
    priority,
    paginated
  );
  const filterAttributes = buildFilterableAttributes<T>(key, filterConfig);

  return {
    key,
    dataIndex: dataIndexKey,
    ...filterAttributes,
    ...sorterAttributes,
    ...rest,
  };
}

function buildFilterableAttributes<T extends object>(
  key: keyof T,
  { selectedFilters, filterOptions }: FilterConfig<T>
) {
  const filteredValue = selectedFilters?.[key];

  return {
    filtered: isFiltered(filteredValue),
    filteredValue: filteredValue || null,
    filters: filterOptions?.[key],
  };
}

const isFiltered = <T>(selectedFilter?: T[]): boolean =>
  !!selectedFilter?.length;

function buildSortableAttributes(
  dataIndex: DataIndex,
  priority?: number,
  paginated?: boolean
) {
  const compare = paginated ? noSorterCompare : compareByKey(dataIndex);
  const sorter = priority
    ? buildMultiSortAttributes(priority, compare)
    : compare;

  return { sorter };
}

const buildMultiSortAttributes = <T>(
  priority: number,
  compare: CompareFn<T>
): TableColumnType<T>['sorter'] => ({
  compare,
  multiple: priority,
});

function compareByKey<T extends object>(dataIndex: DataIndex) {
  return function compare(a: T, b: T): number {
    const valueA = get(a, dataIndex);
    const valueB = get(b, dataIndex);

    if (valueA === valueB) {
      return 0;
    }

    return valueA > valueB ? 1 : -1;
  };
}

// The sorter.compare is mandatory in the antd 4.0.X
// But we don't use it when paginating on the backend.
const noSorterCompare = () => 0;
